<template>
  <main class="container is-fluid">
    <div class="columns is-centered mt-6">
      <div class="column is-narrow">
        <template v-if="loading">
          <p class="is-size-4">Please wait while your {{ resourceType }} is being created</p>
          <span class="loader is-size-3 mt-3 mx-auto"></span>
        </template>
        <div
          class="notification is-danger content"
          v-else
        >
          <p>
            An error occurred while creating your {{ resourceType }}, or it is taking too long to be
            created.<br />
            Please contact your instance administrator.
          </p>
          <p>
            <ItemId
              :item-id="id"
              :label="`${resourceType[0].toUpperCase()}${resourceType.slice(1)} ID:`"
            />
          </p>
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
/**
 * Generic view that allows to await for anything to be created with some replication lag.
 * This should not be used without the `enterprise` feature flag, as replication is only available on Arkindex EE.
 */
import { isAxiosError } from 'axios'
import { onMounted, ref } from 'vue'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import ItemId from '@/components/ItemId.vue'
import { AWAITER_RETRIES, AWAITER_RETRY_DELAY } from '@/config'
import type { UUID } from '@/types'

export interface AwaiterProps {
  /**
   * Display name for the kind of resource we are waiting for: project, process, etc.
   */
  resourceType: string
  /**
   * UUID of the resource that is being retrieved.
   *
   * Sent to the resource retrieval function, and displayed when an error occurs to provide debugging information.
   */
  id: UUID
  /**
   * Function used to attempt to retrieve the resource.
   * @param id UUID of the resource to retrieve.
   * @returns Promise returning a route to redirect to, or `null` if the view should try again.
   * @throws Any promise rejection is treated as an unhandled error. An error message will be shown and no further retries will be made.
   */
  retrieveFunction: (id: UUID) => Promise<RouteLocationRaw | null>
}

const { resourceType, id, retrieveFunction } = defineProps<AwaiterProps>()

const router = useRouter()

const loading = ref(true)
const retries = ref(0)

async function check() {
  try {
    const route = await retrieveFunction(id)
    if (route !== null) {
      router.replace(route)
      return
    }
  } catch (err) {
    /*
     * The corpora store already shows notifications on all errors.
     * We will retry on HTTP or network errors, but we should crash completely on all other errors
     */
    if (!isAxiosError(err)) {
      loading.value = false
      throw err
    }
  }

  retries.value++
  if (retries.value > AWAITER_RETRIES) {
    // Stop trying and show the error message
    loading.value = false
  } else {
    setTimeout(check, AWAITER_RETRY_DELAY)
  }
}

onMounted(check)
</script>
